import { useMemo, useState } from 'react';
import {
    fieldOptions,
    navGroups,
    nestedRecords,
    recordEntries,
    shownRecords,
    visibleRecords,
    type PreviewOp,
    type RecordEntry,
} from './pushRecordsView.js';

/**
 * The push review's record nav, over one environment's dry run — the search, the field filter,
 * which studios are collapsed, and which record is on screen, kept together because the nav and
 * the pane both read it (`PushReviewNav`, `PushRecords`).
 *
 * The host keys its sub-component on the environment, so switching which one the pane is drawn
 * against remounts this hook with nothing searched or picked, rather than a `useEffect` resetting
 * four state slots after the fact (`PushReview`'s environment-scoped section).
 */
export const useRecordsView = (ops: readonly PreviewOp[] | null | undefined) => {
    const entries = useMemo(() => recordEntries(ops), [ops]);
    const fields = useMemo(() => fieldOptions(entries), [entries]);
    const groups = useMemo(() => navGroups(entries), [entries]);

    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState<string | null>(null);
    // A field the environment on screen does not change filters nothing out of it.
    const field = fields.some((option) => option.id === picked) ? picked : null;
    const visible = useMemo(() => visibleRecords(entries, { query, field }), [entries, query, field]);
    const nested = useMemo(() => nestedRecords(visible), [visible]);

    const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
    const shown = useMemo(() => shownRecords(visible, collapsed), [visible, collapsed]);

    const [pickedRecord, setPickedRecord] = useState<string | null>(null);

    const active = visible.some((entry) => entry.id === pickedRecord) ? pickedRecord : (visible[0]?.id ?? null);
    const index = active ? visible.findIndex((entry) => entry.id === active) : -1;
    const allCollapsed = groups.size > 0 && [...groups].every((studio) => collapsed.has(studio));

    return {
        entries,
        fields,
        query,
        setQuery,
        field,
        setField: setPicked,
        visible,
        /** The nav's own rows, a collapsed studio's games left out. */
        shown,
        nested,
        active,
        jump: setPickedRecord,
        next: index >= 0 && index < visible.length - 1 ? () => setPickedRecord(visible[index + 1]!.id) : null,
        prev: index > 0 ? () => setPickedRecord(visible[index - 1]!.id) : null,
        allCollapsed,
        expandAll: () => setCollapsed(new Set()),
        collapseAll: () => setCollapsed(groups),
        clear: () => {
            setQuery('');
            setPicked(null);
        },
    };
};

export type RecordsView = ReturnType<typeof useRecordsView>;

/** The record on screen, if any of the nav's rows is it. */
export const activeEntry = (view: Pick<RecordsView, 'visible' | 'active'>): RecordEntry | null =>
    view.visible.find((entry) => entry.id === view.active) ?? null;
